/* eslint-disable no-console */

import { AdditionalPageOptions } from '../interface';

type Validator = (value: any, key: string, parentKey: string) => boolean;
type Schema<T> = {
    [Property in keyof T]: T[Property] extends string | number | undefined
        ? Validator
        : Schema<T[Property]>
    };
type RecordKey = string | number | symbol;
type SchemaRecord<T> = { [K in RecordKey]: Schema<T[keyof T]> };

const RECORD_MARKER = '_record_' as RecordKey;
const isSchemaRecord = <T>(object: object): object is SchemaRecord<T> =>
    Boolean(Object.keys(object).length) && Object.keys(object)[0] === RECORD_MARKER;
const isObject = (value: unknown): value is object => value !== null && typeof value === 'object';

const validateRecord = <T>(recordValue: T, schema: Schema<T> | Validator, parentKey: string) => {
    for (const [key, value] of Object.entries(recordValue as object)) {
        if (typeof schema === 'function') {
            const isValid: boolean = schema(value, key, parentKey);

            if (!isValid) {
                return false;
            }

            continue;
        }

        // eslint-disable-next-line no-use-before-define
        const isValid = validate(value, schema, parentKey);

        if (!isValid) {
            console.warn(`One of the value of the "${parentKey}.${key}" has invalid type!`);

            return false;
        }
    }

    return true;
};

const validate = <T, K extends keyof T>(objectOrString: T, schema: Schema<T>, parentKey: string) => {
    for (const [key, validator] of Object.entries(schema)) {
        const current = objectOrString[key as keyof T];

        if (typeof validator === 'function') {
            const isValid = validator(current, key, parentKey);

            if (!isValid) {
                return false;
            }
        }

        if (isObject(objectOrString) && isObject(validator)) {
            if (!(key in (objectOrString))) {
                console.warn(`Property "${parentKey}.${key}" is required!`);

                return false;
            }

            const isValid = isSchemaRecord<T>(validator)
                ? validateRecord(current, validator[RECORD_MARKER], key)
                : validate(current, validator as Schema<T[K]>, key);

            if (!isValid) {
                return false;
            }
        }
    }

    return true;
};

const requiredString = (value: string, key: string, parentKey: string) => {
    if (!value) {
        console.warn(`Property "${parentKey}.${key}" is required!`);

        return false;
    }

    if (typeof value !== 'string') {
        console.warn(`Property "${parentKey}.${key}" is not string!`);

        return false;
    }

    return true;
};

// Example, please implement schema accordint to APO interface
const CMS_SCHEMA: Schema<AdditionalPageOptions> = {
    initialLevelId: requiredString,
    levelIds: {
        [RECORD_MARKER]: requiredString,
    },
};

/** Only for testing! */
export const MOCK_SCHEMA: Schema<any> = {
    key: requiredString,
    object: {
        nestedObject: {
            nestedKey: requiredString,
        },
    },
    recordWithString: {
        [RECORD_MARKER]: requiredString,
    },
    recordWithObject: {
        [RECORD_MARKER]: {
            key: requiredString,
        },
    },
};

const isCmsConfigValid = (schema: Schema<unknown> = CMS_SCHEMA) => {
    if (!window.additionalPageOptions) {
        console.warn('AdditionalPageOptions is missing!');

        return false;
    }

    return validate(window.additionalPageOptions, schema, 'additionalPageOptions');
};

export default isCmsConfigValid;
